import { AccordionHeader, AccordionPosition } from 'remote-com-shared/components/Accordion';
import { fluidProp, whenProp } from 'remote-com-shared/style/helpers';
import { sharedTransition } from 'remote-com-shared/style/motion';
import styled, { css } from 'styled-components';

import { RichTextBaseStyle } from '@/src/components/shared/RichText';
import {
  desktopBreakpoint,
  mobileBreakpoint,
  tabletBreakpoint,
} from '@/src/domains/shared/breakpoints';

export const ProgressBarSectionWrapper = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  ${fluidProp(['padding-top', 'padding-bottom'], '50px', '100px')}
  background: var(--colors-purpleMinsk);
  position: relative;
`;

export const ProgressBarSectionHeader = styled.header`
  text-align: center;
  ${fluidProp(['padding-bottom'], '60px', '70px')}
`;

export const ProgressBarContainer = styled.div`
  width: 100%;
  position: absolute;
  z-index: 1;
  max-width: 1055px;
  ${fluidProp('top', '200px', '240px')}
  margin: 0 auto;

  ${tabletBreakpoint(css`
    top: 240px;
  `)}
`;

export const ProgressBar = styled.ul`
  list-style: none;
  counter-reset: step;
  padding-left: 0;

  ${tabletBreakpoint(css`
    display: flex;
    flex-direction: column;
    max-height: 50%;
    padding-top: 40px;
  `)}

  ${mobileBreakpoint(css`
    padding-top: 70px;
  `)}
`;

export const NumberLineSpan = styled.li`
  float: left;
  width: 20%;
  position: relative;
  text-align: center;

  ${tabletBreakpoint(css`
    ${fluidProp('padding-bottom', '240px', '230px')}
  `)}
  ${mobileBreakpoint(css`
    ${fluidProp('padding-bottom', '250px', '200px')}
  `)}

  &:before {
    content: counter(step);
    counter-increment: step;
    width: 30px;
    height: 30px;
    display: block;
    margin: 0 auto 10px auto;
    border-radius: 50%;
    line-height: 26px;
    text-align: center;
    font-weight: bold;
    background: transparent;
    border: 2px solid var(--colors-irisBlue);
    color: var(--colors-irisBlue);
  }

  &:after {
    content: '';
    position: absolute;
    width: 86%;
    height: 3px;
    background: var(--colors-irisBlue);
    top: 15px;
    left: -43%;
    z-index: -1;

    ${tabletBreakpoint(css`
      width: 3px;
      left: 49%;
      content: '';
      position: absolute;
      background: var(--colors-irisBlue);
      z-index: -1;
      ${fluidProp('height', '265px', '210px')}
      ${fluidProp('top', '-265px', '-210px')}
    `)}

    ${mobileBreakpoint(css`
      left: 48%;
      ${fluidProp('height', '260px', '210px')}
      ${fluidProp('top', '-260px', '-210px')}
    `)}
  }

  &:first-child {
    &:before {
      border-color: var(--colors-turquoise);
      background: var(--colors-turquoise);
      color: var(--colors-irisBlue);
    }
    &:after {
      content: none;
    }
  }
`;

export const ActiveNumberLineSpan = styled(NumberLineSpan)`
  & + ${NumberLineSpan}:before {
    border-color: var(--colors-turquoise);
    background: var(--colors-turquoise);
    color: var(--colors-irisBlue);
    ${sharedTransition('color, border-color, background', '0.6s')}
  }

  & + ${NumberLineSpan}:after {
    box-shadow: inset 200px 0 0 0 var(--colors-turquoise);
    ${sharedTransition('box-shadow', '0.6s')}

    ${tabletBreakpoint(css`
      box-shadow: inset 0 300px 0 0 var(--colors-turquoise);
    `)}
  }
`;

export const StepsCardsContainer = styled.div`
  display: flex;

  ${desktopBreakpoint(css`
    flex-direction: column;
  `)}
`;

export const StepCard = styled.article`
  text-align: center;
  margin-left: 25px;
  margin-right: 25px;
  margin-bottom: 80px;
  max-width: 163px;
  opacity: 0.5;

  ${desktopBreakpoint(css`
    max-width: 300px;
  `)}

  ${mobileBreakpoint(css`
    max-width: 220px;
    margin-left: auto;
    margin-right: auto;
  `)}

  ${sharedTransition('opacity', '0.6s')}
  ${whenProp(
    '$active',
    css`
      opacity: 1;
    `
  )}

  &:first-of-type {
    opacity: 1;
  }
`;

export const ImageIconWrapper = styled.div`
  background: var(--colors-irisBlue);
  border-radius: 25px;
  padding: 25px;
  height: 90px;
  width: 90px;

  max-width: 90px;
  margin-left: auto;
  margin-right: auto;
  margin-bottom: 16px;
`;

export const FAQSectionWrapper = styled.section`
  color: var(--colors-darkBlue);
  background-image: ${({ $tone = 'zircon' }) => `var(--colors-${$tone})`};
  ${fluidProp(['padding-top', 'padding-bottom'], '80px', '130px')}
`;
export const FAQAccordionHeader = styled(AccordionHeader)`
  ${fluidProp(['font-size'], '18px', '26px')}

  ${AccordionPosition} {
    --size: 1.26923077em;
    font-size: 0.69230769em;
  }
`;
export const FAQTitleComponent = styled.div`
  ${({ theme }) => theme.typography.scale3}
  ${fluidProp(['font-size'], '24px', '42px')}
  ${fluidProp(['padding-bottom'], '40px', '130px')}
  color: var(--colors-darkBlue);
  text-align: center;

  > * {
    font-size: inherit;
    font-weight: inherit;
    margin: inherit;
  }
`;

export const FAQRichTextWrapper = styled.div`
  ${RichTextBaseStyle}
  --textHighlightColor: var(--colors-irisBlue);
  --textHighlightSecondaryOpacity: 0.4;
`;
